import { useEffect, useState } from "react";
import { Plus, Check, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const TYPE_LABELS = { yillik: "Yıllık", hastalik: "Hastalık", mazeret: "Mazeret", ucretsiz: "Ücretsiz" };
const STATUS_LABELS = { pending: "Bekliyor", approved: "Onaylı", rejected: "Reddedildi" };
const STATUS_TONE = { pending: "secondary", approved: "default", rejected: "destructive" };

export default function Leaves() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: user?.employee_id || "", type: "yillik", start_date: "", end_date: "", note: "" });

  const load = async () => {
    const { data } = await api.get("/leaves");
    setItems(data);
  };

  useEffect(() => {
    load();
    if (user?.role !== "employee") {
      api.get("/employees").then(({ data }) => setEmployees(data));
    }
  }, [user]);

  const submit = async () => {
    try {
      await api.post("/leaves", form);
      toast.success("İzin talebi oluşturuldu");
      setOpen(false);
      setForm({ employee_id: user?.employee_id || "", type: "yillik", start_date: "", end_date: "", note: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/leaves/${id}`, { status });
    toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
    load();
  };

  const isManager = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">İzinler</h1>
          <p className="text-muted-foreground text-sm mt-1">İzin talep yönetimi</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="new-leave-btn"><Plus className="w-4 h-4" /> Yeni İzin</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Yeni İzin Talebi</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {isManager && (
                <div className="space-y-1.5">
                  <Label>Personel</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger data-testid="leave-employee"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Başlangıç</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start" />
                </div>
                <div className="space-y-1.5">
                  <Label>Bitiş</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Not</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="leave-note" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
              <Button onClick={submit} data-testid="leave-submit">Oluştur</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="glass-card rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Personel</TableHead>
              <TableHead>Tür</TableHead>
              <TableHead>Başlangıç</TableHead>
              <TableHead>Bitiş</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead>Not</TableHead>
              {isManager && <TableHead className="text-right">İşlem</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((l) => (
              <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                <TableCell className="font-medium">{l.employee?.first_name} {l.employee?.last_name}</TableCell>
                <TableCell>{TYPE_LABELS[l.type]}</TableCell>
                <TableCell className="font-mono text-sm">{l.start_date}</TableCell>
                <TableCell className="font-mono text-sm">{l.end_date}</TableCell>
                <TableCell><Badge variant={STATUS_TONE[l.status]}>{STATUS_LABELS[l.status]}</Badge></TableCell>
                <TableCell className="text-muted-foreground text-sm">{l.note}</TableCell>
                {isManager && (
                  <TableCell className="text-right">
                    {l.status === "pending" && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => setStatus(l.id, "approved")} className="text-emerald-500"><Check className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => setStatus(l.id, "rejected")} className="text-red-500"><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={isManager ? 7 : 6} className="text-center text-muted-foreground py-10">İzin kaydı yok.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
